import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { statusService, type StatusEntityType } from '@/services';

interface Status {
  id: string;
  name: string;
  seq_num: number;
}

interface StatusDeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: Status;
  entityType: string;
  companyId: string;
  availableStatuses: Status[];
  onConfirmDelete: (reassignToStatusId?: string) => void;
  isDeleting: boolean;
}

const ENTITY_LABEL_MAP: Record<string, string> = {
  invoice: 'Invoices',
  vendor: 'Vendors',
  price_book: 'Price Books',
  ticket: 'Field Tickets',
};

export default function StatusDeleteConfirmDialog({
  open,
  onOpenChange,
  status,
  entityType,
  companyId,
  availableStatuses,
  onConfirmDelete,
  isDeleting,
}: StatusDeleteConfirmDialogProps) {
  const [selectedStatusId, setSelectedStatusId] = useState<string>('');

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedStatusId('');
    }
  }, [open]);

  const entityLabel = ENTITY_LABEL_MAP[entityType] || entityType;

  // Query to count records using this status via service
  const { data: recordCount, isLoading: isCountLoading } = useQuery({
    queryKey: ['status-record-count', status.id, entityType, companyId],
    queryFn: async () => {
      const result = await statusService.countRecordsByStatus(status.id, entityType as StatusEntityType);
      if (result.error) throw result.error;
      return result.count;
    },
    enabled: open,
  });

  const hasRecords = (recordCount ?? 0) > 0;
  const hasNoReplacementStatuses = availableStatuses.length === 0;

  const handleConfirm = () => {
    if (hasRecords) {
      onConfirmDelete(selectedStatusId);
    } else {
      onConfirmDelete();
    }
  };

  // Determine if the delete button should be disabled
  const isDeleteDisabled = isDeleting || (hasRecords && !selectedStatusId) || hasNoReplacementStatuses;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Status</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {isCountLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : hasRecords ? (
                <>
                  <p>
                    This status of <strong>{status.name}</strong> is used on:
                  </p>
                  <p className="font-medium">
                    {recordCount} {entityLabel}
                  </p>
                  {hasNoReplacementStatuses ? (
                    <p className="text-destructive">
                      Cannot delete this status because there are no other statuses to reassign records to. 
                      Please create another status first.
                    </p>
                  ) : (
                    <>
                      <p>To what status should these records be reassigned:</p>
                      <Select value={selectedStatusId} onValueChange={setSelectedStatusId}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a status..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableStatuses.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </>
              ) : (
                <p>
                  Are you sure you want to delete the status <strong>{status.name}</strong>? 
                  This will also delete all associated workflow transitions. This action cannot be undone.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleteDisabled}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Deleting...' : hasRecords ? 'Delete & Reassign' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
